import React from "react";
import ClickableImage from "./ClickableImage";
import PhotoThumbnailWithMarker from "./PhotoThumbnailWithMarker";
import { translate } from "../types/EnumTranslations";
import { getMaterialTranslation } from "../contexts/MaterialsContext";
import COLORS, { countWindowsForComponent, countDoorsForComponent, countSheetMetalForComponent, countChimneysForComponent, countDrainageForComponent } from "../Helpers/utils";
import "./ComponentCard.css";
import { BaseComponentImpl } from "../types/implementations";
import { BaseType } from "../types/Enums";
import { Building, PhotoModel, PhotoLocation } from "../types/protocols";
import { useBuildComponentTypes } from "../contexts/BuildComponentTypesContext";
import { RiskBadge } from "../Pages/BuildingDetails/RisksOverview/RisksOverviewList";
import { primaryPhotoLocation } from "../Helpers/photoLocations";

export interface ComponentCardProps {
    component: BaseComponentImpl;
    building: Building;
    buildingKey: string;
    /** Also called for a middle click; the event lets a caller open a new tab (`useOpenPath`). */
    onNavigate: (componentKey: string, event?: React.MouseEvent) => void;
    onMouseEnter?: (componentKey: string) => void;
    onMouseLeave?: () => void;
    isHovered?: boolean;
    isSelected?: boolean;
    disableThumbnails?: boolean;
    onPhotoClick?: (photo: PhotoModel, url: string) => void;
    onPhotoLocationClick?: (photoLocation: PhotoLocation) => void;
}


const maxPhotos = 3
const photoSize = 112

const ComponentCard: React.FC<ComponentCardProps> = ({
    component,
    building,
    buildingKey,
    onNavigate,
    onMouseEnter,
    onMouseLeave,
    isHovered,
    isSelected,
    disableThumbnails = false,
    onPhotoClick,
    onPhotoLocationClick,
}) => {
    const { getTranslation } = useBuildComponentTypes();
    const primaryPin = primaryPhotoLocation(component, building);
    return (
        <div
            className={`component-card dropshadow dropshadow-hover ${isHovered ? 'highlighted' : ''} ${isSelected ? 'selected' : ''}`}
            style={{
                fontSize: '9pt',
                padding: '20px',
                paddingTop: '26px',
                transform: isHovered ? 'scale(1.02)' : 'scale(1)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                ...(isSelected ? { boxShadow: `rgba(0, 0, 0, 0.2) 0 6px 16px, 0 0 0 3px ${COLORS.primaryDark}` } : {})
            }}
            onClick={(e) => onNavigate(component.key, e)}
            onAuxClick={(e) => { if (e.button === 1) onNavigate(component.key, e); }}
            onMouseEnter={() => onMouseEnter?.(component.key)}
            onMouseLeave={() => onMouseLeave?.()}
        >
            <div className="component-card-timestamp">
                Uppdaterad {component.lastUpdated()}
            </div>

            <h4>{component.displayName(building)}</h4>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span className="component-card-label">{getTranslation(component.specificType?.value ?? undefined, component.type)}</span>
                {
                    component.material?.value &&
                    (component.material?.value.map((material) => (
                        <span className="component-card-label">{getMaterialTranslation(material)}</span>
                    )))
                }

                {
                    component.areaShapes?.value && component.totalArea(building) &&
                    <span className="component-card-label">{component.totalArea(building)?.toFixed(1)} kvm</span>
                }

                {
                    (() => {
                        const windowCount = countWindowsForComponent(component.key, building);
                        return windowCount > 0 ? (
                            <span className="component-card-label">{windowCount} fönster</span>
                        ) : null;
                    })()
                }

                {
                    (() => {
                        const doorCount = countDoorsForComponent(component.key, building);
                        return doorCount > 0 ? (
                            <span className="component-card-label">{doorCount} {doorCount === 1 ? 'dörr' : 'dörrar'}</span>
                        ) : null;
                    })()
                }

                {
                    (() => {
                        // Only show sheet metal count for Roof and Facade components
                        if (component.type === BaseType.Roof || component.type === BaseType.Facade) {
                            const sheetMetalCount = countSheetMetalForComponent(component.key, building);
                            return sheetMetalCount > 0 ? (
                                <span className="component-card-label">{sheetMetalCount} plåtarbeten</span>
                            ) : null;
                        }
                        return null;
                    })()
                }

                {
                    (() => {
                        // Show chimney count for Roof and Facade components
                        if (component.type === BaseType.Roof || component.type === BaseType.Facade) {
                            const chimneyCount = countChimneysForComponent(component.key, building);
                            return chimneyCount > 0 ? (
                                <span className="component-card-label">{chimneyCount} {translate(BaseType.Chimney).toLowerCase()}</span>
                            ) : null;
                        }
                        return null;
                    })()
                }

                {
                    (() => {
                        // Show drainage count for Roof and Facade components
                        if (component.type === BaseType.Roof || component.type === BaseType.Facade) {
                            const drainageCount = countDrainageForComponent(component.key, building);
                            return drainageCount > 0 ? (
                                <span className="component-card-label">{drainageCount} {translate(BaseType.RoofDrainage).toLowerCase()}</span>
                            ) : null;
                        }
                        return null;
                    })()
                }
            </div>

            {(component.hazardous?.value || component.buildingRisks?.value) && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                    {component.hazardous?.value && <RiskBadge type="hazard" />}
                    {component.buildingRisks?.value && (
                        (component.buildingRisks.value.risks && component.buildingRisks.value.risks.length > 0) || component.buildingRisks.value.customRisk
                    ) && <RiskBadge type="riskConstruction" />}
                </div>
            )}

            {
                component.photos &&
                <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: '20px', cursor: 'pointer' }}
                    onClick={onPhotoClick ? (e) => e.stopPropagation() : undefined}
                >
                    {component.photos && buildingKey && Object.entries(component.photos).slice(0, maxPhotos).map(([key, photo]) => (
                        <ClickableImage
                            key={key}
                            buildingKey={buildingKey}
                            photo={photo}
                            disableClick={!onPhotoClick}
                            onImageClick={onPhotoClick}
                            size={photoSize}
                        />
                    ))}
                    {component.photos && Object.keys(component.photos).length > maxPhotos && (
                        <div
                            style={{
                                width: `${photoSize}px`,
                                height: `${photoSize}px`,
                                backgroundColor: '#cccccc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14pt',
                                borderRadius: '4px'
                            }}
                        >
                            +{Object.keys(component.photos).length - maxPhotos}
                        </div>
                    )}
                </div>
            }

            {
                primaryPin &&
                <div style={{ marginTop: '20px' }}>
                    <div style={{ fontSize: '8pt', color: '#666', marginBottom: '4px' }}>Position i foto</div>
                    <PhotoThumbnailWithMarker
                        photoLocation={primaryPin}
                        size={photoSize}
                        disableClick={disableThumbnails && !onPhotoLocationClick}
                        onClick={onPhotoLocationClick ? () => onPhotoLocationClick(primaryPin) : undefined}
                    />
                </div>
            }
        </div>
    );
};

export default ComponentCard;
